import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { setSelectedCategory, fetchMenuDataRequest, MenuItem } from '../redux/slices/menuSlice';
import { addToCart, openCart, clearCart } from '../redux/slices/cartSlice';
import { fetchComboRequest } from '../redux/slices/comboSlice';
import { replaceMenuWidgetType, updateSiteContentRequest } from '../redux/slices/siteContentSlice';
import { fetchPaymentStatusRequest, clearPaymentStatusState } from '../redux/slices/orderSlice';
import { 
  createCartItem,
  createBuilderCartItem,
  formatPreviousCustomizations
} from '../utils/cartUtils';
import { useCartQuantityControls } from '../hooks/useCartQuantityControls';
import { useMenuItemCart } from '../hooks/useMenuItemCart';
import { parsePrice } from '../utils/priceUtils';
import { FiLayout } from 'react-icons/fi';
import {
  StatusMessage,
  MenuGrid,
  CategoryFilter,
  MenuSearch,
  ItemDetailsModal,
  // PageBackButton
} from '../components';
import MenuStyleSelectionModal from '../components/MenuStyleSelectionModal';
import { MenuItemCardVariant } from '../components/MenuGrid';
import { AddToCartModal } from '../components/AddToCartModal';
import { ModifierBuilderModal } from '../components/ModifierBuilderModal';
import { ModifierBuilder } from '../components/ModifierBuilder';
import { RepeatCustomisationModal } from '../components/RepeatCustomisationModal';
import { DeleteVariantModal } from '../components/DeleteVariantModal';
import { PaymentSuccessPopup, PaymentFailedProcessingPopup } from '../components/checkout';
import DietaryFilters, { DietaryFilter, useDietaryFilterLogic, applyDietaryFilters } from '../components/DietaryFilters';
// import MenuToggle from '../components/MenuToggle';
import ComboCard from '../components/ComboCard';
import BuilderCategoryFilter from '../components/BuilderCategoryFilter';
import { fetchTpnConfigRequest } from '../redux/slices/tpnConfigSlice';
import { useTpnCart } from '../hooks/useTpnCart';
import { useNavSpacing } from '../hooks/useNavSpacing';
import { useHeroBarHeight } from '../hooks/useHeroBarHeight';
import { getAppMode } from '../utils/appConfig';

const MenuPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const appNavigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const { loading, error, data: menuData, selectedCategory } = useAppSelector(state => state.menu);
  const { loading: comboLoading, data: comboData } = useAppSelector(state => state.combo);
  const { data: siteContentData, updateLoading } = useAppSelector(state => state.siteContent);
  const { isEditControl } = useAppSelector(state => state.editControl);
  const { paymentStatusResponse, paymentStatusError } = useAppSelector(state => state.order);
  const { selectedRestaurant } = useAppSelector(state => state.restaurant);
  const { items: cartItems } = useAppSelector(state => state.cart);
  const shouldShowCart = useTpnCart(true);

  // Track if we're saving menu style to close modal after success
  const isSavingMenuStyle = useRef(false);
  const prevUpdateLoading = useRef<boolean>(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [modalItem, setModalItem] = useState<MenuItem | null>(null);
  const [isModifierBuilderOpen, setIsModifierBuilderOpen] = useState(false);
  // Item Details Modal state
  const [detailModalItem, setDetailModalItem] = useState<MenuItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [activeDietaryFilters, setActiveDietaryFilters] = useState<Set<DietaryFilter>>(new Set());
  const [isComboMode, setIsComboMode] = useState(false);
  const [showPaymentFailedPopup, setShowPaymentFailedPopup] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [selectedBuilderItemId, setSelectedBuilderItemId] = useState<string | null>(null);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  
  const { handleDietaryFilterToggle } = useDietaryFilterLogic();

  // Use reusable navigation spacing hook
  const navSpacing = useNavSpacing();
  
  // Use custom hook for hero bar height (follows React best practices)
  const heroBarHeight = useHeroBarHeight();

  // Handle menu style selection - save and close after API success
  const handleSelectWidget = useCallback((widgetType: string) => {
    // Mark that we're saving to close modal after API completes
    isSavingMenuStyle.current = true;
    // Update Redux
    dispatch(replaceMenuWidgetType(widgetType));
    // Save to API
    dispatch(updateSiteContentRequest());
    // DON'T close modal here - let useEffect close it after API success
  }, [dispatch]);

  // Close modal after API successfully saves menu style
  useEffect(() => {
    // Only close when loading transitions from true → false
    if (isSavingMenuStyle.current && prevUpdateLoading.current && !updateLoading) {
      setShowWidgetModal(false);
      isSavingMenuStyle.current = false;
    }
    // Track previous loading state
    prevUpdateLoading.current = updateLoading;
  }, [updateLoading]);

  // Dynamic cardVariant based on Redux menuWidgetTypes
  const cardVariant = useMemo((): MenuItemCardVariant => {
    if (!siteContentData?.menuWidgetTypes || siteContentData.menuWidgetTypes.length === 0) {
      return 'modern'; // default
    }

    const menuType = siteContentData.menuWidgetTypes[0]; // Get first menu widget type

    // Map widget types to card variants
    if (menuType.includes('Basic')) return 'basic';
    if (menuType.includes('Classic')) return 'classic';
    if (menuType.includes('Prime')) return 'prime';
    return 'modern'; // default for Modern or unknown types
  }, [siteContentData?.menuWidgetTypes]);

  // Hybrid payment verification - URL params first, then API fallback
  useEffect(() => {
    // Skip ALL payment verification in edit/preview modes
    const appMode = getAppMode();
    if (appMode === 'edit' || appMode === 'preview') {
      console.log('🚫 Skipping payment verification in', appMode, 'mode');
      return;
    }
    
    console.log('🔍 MenuPage loaded with URL:', window.location.href);
    console.log('🔍 Restaurant ID from URL:', id);
    
    const searchParams = new URLSearchParams(window.location.search);
    const responseCode = searchParams.get('responseCode');
    const transactionId = searchParams.get('transactionId');
    const transactionReferenceId = searchParams.get('transactionReferenceId');
    const paymentStatus = searchParams.get('payment_status');

    console.log('🔍 Payment parameters found:', {
      responseCode,
      transactionId,
      transactionReferenceId,
      paymentStatus,
      hasAnyPaymentParam: !!(responseCode || transactionId || transactionReferenceId || paymentStatus)
    });

    // Option 1: Check URL parameters first (old logic - fast, no API call)
    if (responseCode === '200' || paymentStatus?.toLowerCase() === 'success') {
      console.log('✅ Payment success from URL parameters');
      setShowPaymentSuccessPopup(true);
    } else if (responseCode === '400') {
      console.log('❌ Payment failed from URL parameters:', responseCode);
      setShowPaymentFailedPopup(true);
    }
    // Option 1.5: Verify transactionId or transactionReferenceId via API (Square/other payments)
    else if ((transactionId || transactionReferenceId) && selectedRestaurant?.restaurant_id) {
      console.log('🔍 Transaction ID found - verifying via API');
      dispatch(fetchPaymentStatusRequest({ 
        order_id: transactionId || transactionReferenceId || '',
        restaurant_id: selectedRestaurant.restaurant_id 
      }));
    }
    // Option 2: No responseCode but restaurant ID exists - verify via API (new logic - secure)
    else if (id && selectedRestaurant?.restaurant_id) {
      console.log('💳 No responseCode found - calling API to verify payment status');
      console.log('🏪 Using restaurant_id from Redux:', selectedRestaurant.restaurant_id);
      dispatch(fetchPaymentStatusRequest({ 
        order_id: id,
        restaurant_id: selectedRestaurant.restaurant_id 
      }));
    } else {
      console.log('🔍 No payment parameters or ID found in URL');
    }
  }, [id, selectedRestaurant, dispatch]);

  // Handle payment status API response
  useEffect(() => {
    if (paymentStatusResponse) {
      console.log('📥 Payment status response received:', paymentStatusResponse);
      
      // Only show popup if 'status' key exists in response
      if ('status' in paymentStatusResponse) {
        // Check status field (boolean) or payment_status field (string)
        const isSuccess = paymentStatusResponse.status === true || 
                          paymentStatusResponse.payment_status === 'success';
        
        if (isSuccess) {
          console.log('✅ Payment verified as successful');
          setShowPaymentSuccessPopup(true);
        } else {
          console.log('❌ Payment failed or invalid');
          setShowPaymentFailedPopup(true);
        }
      } else {
        console.log('⚠️ No status key in response - skipping popup');
      }
      
      // Clear payment status state after handling
      dispatch(clearPaymentStatusState());
    }
  }, [paymentStatusResponse, dispatch]);

  // Handle payment status API errors
  useEffect(() => {
    if (paymentStatusError) {
      console.error('❌ Payment status API error:', paymentStatusError);
      setShowPaymentFailedPopup(true);
      
      // Clear payment status state after handling
      dispatch(clearPaymentStatusState());
    }
  }, [paymentStatusError, dispatch]);
  
  // Fetch menu data and combo data on component mount if not already loaded
  useEffect(() => {
    dispatch(fetchMenuDataRequest());
    dispatch(fetchTpnConfigRequest())
    dispatch(fetchComboRequest());
  }, [dispatch]);

  const handleCategoryChange = useCallback((category: string | null) => {
    dispatch(setSelectedCategory(category));
  }, [dispatch]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleItemClick = useCallback((item: MenuItem) => {
    // Open ItemDetailsModal instead of navigating
    setDetailModalItem(item);
    setIsDetailModalOpen(true);
  }, []);

  // 🛒 Use reusable cart hook - no auto-open cart
  const { 
    handleAddToCart, 
    modalProps,
    openModal
  } = useMenuItemCart({ 
    onBuilderItemClick: (item) => {
      // Custom handler for builder items (salad bar)
      setModalItem(item);
      setIsModifierBuilderOpen(true);
    }
  });

  const handleModifierBuilderClose = useCallback(() => {
    setIsModifierBuilderOpen(false);
  }, []);

  const handleModifierBuilderAddToCart = useCallback((
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: any[]
  ) => {
    const cartItem = createBuilderCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    dispatch(openCart());
  }, [dispatch]);

  // ItemDetailsModal handlers
  const handleDetailModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setDetailModalItem(null);
  }, []);

  const handleDetailModalAddToCart = useCallback((
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: any[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    // Close modal
    setIsDetailModalOpen(false);
    setDetailModalItem(null);
  }, [dispatch]);

  const handleRecommendedItemClick = useCallback((item: MenuItem) => {
    // Switch to clicked recommended item
    setDetailModalItem(item);
  }, []);

  // Handle dietary filter toggle using the logic from DietaryFilters component
  const onDietaryFilterToggle = useCallback((filter: DietaryFilter) => {
    handleDietaryFilterToggle(filter, activeDietaryFilters, setActiveDietaryFilters);
  }, [handleDietaryFilterToggle, activeDietaryFilters]);

  const enabledCombos = useMemo(() => {
    if (!comboData || !Array.isArray(comboData)) return [];
    return comboData.filter((combo: any) => combo.is_enabled === true);
  }, [comboData]);

  // Handle combo add to cart
  const handleComboAddToCart = useCallback((combo: any) => {
    const comboCartItem = {
      item: {
        pk_id: `combo-${combo.combo_id}`,
        name: combo.title,
        description: combo.description,
        price: combo.combo_price.replace('$', ''),
        image: combo.combo_image,
        is_combo: true,
        combo_data: combo,
      },
      selected: {
        spice: null,
        modifiers: [],
      },
      totalPrice: parsePrice(combo.combo_price),
      cartItemId: `combo-${combo.combo_id}-${Date.now()}`,
      quantity: 1,
    };
    console.log()
    dispatch(addToCart(comboCartItem));
    dispatch(openCart());
  }, [dispatch]);

  // Filter combos based on search and category
  const filteredCombos = useMemo(() => {
    let filtered = enabledCombos;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((combo: any) =>
        combo.title.toLowerCase().includes(query) ||
        combo.description.toLowerCase().includes(query)
      );
    }

    // Apply default sorting by title
    return [...filtered].sort((a: any, b: any) => a.title.localeCompare(b.title));
  }, [enabledCombos, searchQuery]);

  // Pre-filter menu items to match CategoryFilter logic
  const availableMenuItems = useMemo(() => {
    if (!menuData) return [];
    return menuData.filter(item => 
      item.is_enabled && 
      item.is_item_verified
    );
  }, [menuData]);

  // Filter and sort menu items
  const filteredAndSortedMenuItems = useMemo(() => {
    if (!availableMenuItems) return [];

    let filtered = availableMenuItems;

    // Apply dietary filters first
    if (activeDietaryFilters.size > 0) {
      filtered = applyDietaryFilters(availableMenuItems, comboData || [], activeDietaryFilters);
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(item =>
        item.category === selectedCategory ||
        item.level1_category === selectedCategory ||
        item.level2_category === selectedCategory
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    // Apply default sorting by name
    const sorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name));

    return sorted;
  }, [availableMenuItems, selectedCategory, searchQuery, activeDietaryFilters, comboData]);

  // Separate salad items from regular items
  const { saladItems, regularItems } = useMemo(() => {
    const salads: MenuItem[] = [];
    const regular: MenuItem[] = [];
    
    filteredAndSortedMenuItems.forEach(item => {
      if (item.builder_type?.toLowerCase() === 'salad') {
        salads.push(item);
      } else {
        regular.push(item);
      }
    });
    
    return { saladItems: salads, regularItems: regular };
  }, [filteredAndSortedMenuItems]);

  // Auto-select first builder item when salad items load (but NOT category)
  useEffect(() => {
    if (saladItems.length > 0) {
      const firstSaladItem = saladItems[0];
      
      // Check if currently selected item exists in filtered list
      const isCurrentItemInList = saladItems.some(
        item => item.pk_id === selectedBuilderItemId
      );
      
      // 3. Auto-select first builder item if:
      //    - No item selected, OR
      //    - Selected item not in current category (category changed)
      if (!selectedBuilderItemId || !isCurrentItemInList) {
        setSelectedBuilderItemId(firstSaladItem.pk_id);
      }
    }
  }, [saladItems, selectedBuilderItemId, dispatch]);

  // Filter salad items by selected builder item ID
  const filteredSaladItems = useMemo(() => {
    if (!selectedBuilderItemId) {
      return []; // No item selected = no items shown (accordion collapsed)
    }
    
    return saladItems.filter(item => item.pk_id === selectedBuilderItemId);
  }, [saladItems, selectedBuilderItemId]);

  const handleBuilderItemChange = useCallback((itemId: string | null) => {
    setSelectedBuilderItemId(itemId);
  }, []);

  // Use smart cart quantity controls hook
  const {
    isRepeatCustomizationModalOpen,
    itemToRepeatOrCustomize,
    isDeleteVariantModalOpen,
    variantsToDelete,
    itemNameForDelete,
    handleUpdateQuantity,
    handleIncrementItemQuantity,
    handleDecrementItemQuantity,
    handleRepeatPreviousCustomization,
    handleCustomizeNewVariant,
    closeRepeatModal,
    closeDeleteModal,
  } = useCartQuantityControls(cartItems, handleAddToCart, openModal);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 md:pt-36 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-48 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <StatusMessage message={`Error Loading Menu: ${error}`} type="error" />;
  }

  if (!menuData || menuData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 md:pt-36 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🍽️</div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Menu Available</h2>
            <p className="text-gray-600">
              Menu items are not available at the moment. Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white" style={{ paddingTop: `${navSpacing}px` }}>
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Title and Search Section */}
          <div className="mb-6">
            {/* Title - Always at top on mobile, desktop: same row with search */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold">Our Menu</h1>
              
              {/* Search and Filters - Hidden on mobile, shown on desktop in header */}
              <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
                {/* Search Bar */}
                <div className="w-full max-w-md">
                  <MenuSearch
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                  />
                </div>

                {/* Dietary Filters */}
                {!isComboMode && (
                  <DietaryFilters
                    activeDietaryFilters={activeDietaryFilters}
                    onDietaryFilterToggle={onDietaryFilterToggle}
                    hasEnabledCombos={false}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Mobile Search and Filters - Above Category Filter */}
          {!isComboMode && (
            <div className="md:hidden space-y-4">
              {/* Dietary Filters */}
              <DietaryFilters
                activeDietaryFilters={activeDietaryFilters}
                onDietaryFilterToggle={onDietaryFilterToggle}
                hasEnabledCombos={false}
              />

              {/* Search Bar */}
              <MenuSearch
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
              />
            </div>
          )}

          {/* Category Filter - Sticky below nav - Only show for individual items */}
          {!isComboMode && (
            <div 
              className="sticky z-[45] -mx-4 px-4 mb-8 bg-white"
              style={{ top: `${navSpacing - heroBarHeight}px` }}
            >
              <CategoryFilter
                menuItems={menuData}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
              />
            </div>
          )}

          {/* Menu Toggle - Show if combo data exists */}
          {/* {hasComboData && (
            <MenuToggle
              isComboMode={isComboMode}
              onToggle={setIsComboMode}
              comboCount={totalComboCount}
            />
          )} */}



          {/* Results Count */}
          {/* <div className="mb-4">
            <p className="text-sm text-gray-600">
              {isComboMode
                ? `Showing ${filteredCombos.length} of ${enabledCombos.length} combos`
                : `Showing ${filteredAndSortedMenuItems.length} of ${menuData.length} items`
              }
            </p>
          </div> */}

          {/* Content Display */}
          <div>
            {isComboMode ? (
              // Combo Grid
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCombos.length > 0 ? (
                  filteredCombos.map((combo: any) => (
                    <ComboCard
                      key={combo.combo_id}
                      combo={combo}
                      variant="basic"
                      onAddToCart={shouldShowCart ? handleComboAddToCart : undefined}
                    />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <div className="text-6xl mb-4">🍽️</div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Combos Available</h2>
                    <p className="text-gray-600">
                      {searchQuery ? 'No combos match your search.' : 'No combo offers are currently available.'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Salad Bar Items - With Category Filter */}
                {saladItems.length > 0 && (
                  <div className="mb-8">
                    {/* Builder Item Filter */}
                    <BuilderCategoryFilter
                      builderItems={saladItems}
                      selectedItemId={selectedBuilderItemId}
                      onItemChange={handleBuilderItemChange}
                    />
                    
                    {/* Filtered Salad Items - Only shown when category is selected */}
                    {filteredSaladItems.length > 0 && (
                      <div className="mt-6">
                        {filteredSaladItems.map(item => (
                          <ModifierBuilder
                            key={item.pk_id}
                            item={item}
                            onAddToCart={handleModifierBuilderAddToCart}
                            autoExpand={true}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Regular Menu Items Grid */}
                {regularItems.length > 0 && (
                  <MenuGrid
                    menuItems={regularItems}
                    selectedCategory={null}
                    searchQuery=""
                    variant={cardVariant}
                    onItemClick={handleItemClick}
                    onAddToCart={shouldShowCart ? handleAddToCart : undefined}
                    cartItems={cartItems}
                    onIncrementQuantity={handleIncrementItemQuantity}
                    onDecrementQuantity={handleDecrementItemQuantity}
                  />
                )}

                {/* Select Menu Card Style Button - Floating - Only show when edit control is enabled */}
                {isEditControl && (
                  <button
                    onClick={() => setShowWidgetModal(true)}
                    className="fixed bottom-6 right-6 px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 z-40"
                    style={{ backgroundColor: '#EE4446' }}
                    aria-label="Select menu card style"
                  >
                    <FiLayout className="w-5 h-5 text-white" />
                    <span className="text-white font-medium text-sm whitespace-nowrap">Select Menu Card Style</span>
                  </button>
                )}

                {/* Empty State */}
                {saladItems.length === 0 && regularItems.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🍽️</div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Items Found</h2>
                    <p className="text-gray-600">
                      {searchQuery ? 'No items match your search.' : 'No menu items are currently available.'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add to Cart Modal */}
      <AddToCartModal {...modalProps} />

      {/* Modifier Builder Modal - Uses Dynamic Item Data */}
      <ModifierBuilderModal
        isOpen={isModifierBuilderOpen}
        item={modalItem || {} as MenuItem}
        onClose={handleModifierBuilderClose}
        onAddToCart={handleModifierBuilderAddToCart}
      />
      <PaymentFailedProcessingPopup
        isOpen={showPaymentFailedPopup}
        onClose={() => {
          setShowPaymentFailedPopup(false);
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
        onTryAgain={() => {
          setShowPaymentFailedPopup(false);
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
      />
      <PaymentSuccessPopup
        isOpen={showPaymentSuccessPopup}
        onClose={() => {
          setShowPaymentSuccessPopup(false);
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
        onContinue={() => {
          setShowPaymentSuccessPopup(false);
          dispatch(clearCart());
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
      />

      {/* Menu Style Selection Modal */}
      <MenuStyleSelectionModal
        isOpen={showWidgetModal}
        onClose={() => setShowWidgetModal(false)}
        onSelectStyle={handleSelectWidget}
        isLoading={updateLoading}
      />

      {/* Item Details Modal - Opens on card click */}
      <ItemDetailsModal
        isOpen={isDetailModalOpen}
        item={detailModalItem}
        onClose={handleDetailModalClose}
        onAddToCart={shouldShowCart ? handleDetailModalAddToCart : undefined}
        menuData={menuData}
        onRecommendedItemClick={handleRecommendedItemClick}
      />

      {/* Repeat Customisation Modal - Shows when user increments customized item */}
      <RepeatCustomisationModal
        isOpen={isRepeatCustomizationModalOpen}
        itemName={itemToRepeatOrCustomize?.item?.name || ''}
        previousSelections={itemToRepeatOrCustomize ? formatPreviousCustomizations(itemToRepeatOrCustomize) : []}
        spiceLevel={itemToRepeatOrCustomize?.selected?.spice}
        modifiers={itemToRepeatOrCustomize?.selected?.modifiers}
        onRepeat={handleRepeatPreviousCustomization}
        onChoose={handleCustomizeNewVariant}
        onClose={closeRepeatModal}
      />

      {/* Delete Variant Modal - Shows when user decrements item with multiple customizations */}
      <DeleteVariantModal
        isOpen={isDeleteVariantModalOpen}
        itemName={itemNameForDelete}
        basePrice={variantsToDelete[0]?.item?.price ? parsePrice(variantsToDelete[0].item.price) : 0}
        variants={variantsToDelete}
        onUpdateQuantity={handleUpdateQuantity}
        onClose={closeDeleteModal}
      />
    </>
  );
};

export default MenuPage;
